import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMe } from '../helpers/user'
import { orpcClient as client } from './client'
import {
  questV2ListCategoriesOptions,
  questV2ListCategoriesQueryKey,
  questV2ListQuestsOptions,
  questV2ListQuestsQueryKey,
  questV2ClaimRewardMutation,
  questV2ReportUrlVisitMutation,
  questV2ReportSocialFollowMutation,
  questV2ListFollowedPlatformsOptions,
  questV2ListFollowedPlatformsQueryKey,
} from './generated/@tanstack/react-query.gen'

/** Hook to list quest categories */
export function useQuestV2Categories() {
  const { me } = useMe()
  return useQuery({
    ...questV2ListCategoriesOptions({ client }),
    select: data => data.data,
    enabled: Boolean(me),
    refetchOnWindowFocus: 'always',
  })
}

/** Hook to list quests for the current user */
export function useQuestV2Quests(options?: {
  invisibleQuests?: 'include' | 'exclude'
}) {
  const { me } = useMe()
  return useQuery({
    ...questV2ListQuestsOptions({
      client,
      query: { invisibleQuests: options?.invisibleQuests },
    }),
    select: data => data.data,
    enabled: Boolean(me),
    refetchOnWindowFocus: 'always',
  })
}

/** Hook to claim reward for a completed quest */
export function useClaimQuestV2Reward() {
  const queryClient = useQueryClient()
  return useMutation({
    ...questV2ClaimRewardMutation({ client }),
    onSettled: async () => {
      await invalidateQuestV2Data(queryClient)
    },
  })
}

/** Hook to report that the user visited a quest URL, advancing progress */
export function useReportQuestV2UrlVisit() {
  const queryClient = useQueryClient()
  return useMutation({
    ...questV2ReportUrlVisitMutation({ client }),
    onSettled: async () => {
      await invalidateQuestV2Data(queryClient)
    },
  })
}

/** Hook to report that the user followed a social platform */
export function useReportSocialFollow() {
  const queryClient = useQueryClient()
  return useMutation({
    ...questV2ReportSocialFollowMutation({ client }),
    onSettled: async () => {
      await invalidateQuestV2Data(queryClient)
    },
  })
}

/** Hook to list platforms the user has followed */
export function useFollowedPlatforms() {
  const { me } = useMe()
  return useQuery({
    ...questV2ListFollowedPlatformsOptions({ client }),
    select: data => data.data,
    enabled: Boolean(me),
    refetchOnWindowFocus: 'always',
  })
}

/** Invalidate all quest v2 caches */
export async function invalidateQuestV2Data(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: questV2ListCategoriesQueryKey({ client }),
    }),
    queryClient.invalidateQueries({
      queryKey: questV2ListQuestsQueryKey({ client }),
    }),
    queryClient.invalidateQueries({
      queryKey: questV2ListFollowedPlatformsQueryKey({ client }),
    }),
  ])
}
